import React, { useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, ButtonBase, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { format } from 'date-fns';
import { TaskActions } from '../actions/useTaskActions';
import { ClientTasksResult } from '../tasks/useClientTasks';
import { INK, panelSx, SourceNotice } from '../ui';
import { ClientDetailPanel } from './ClientDetailPanel';
import { InboxList } from './InboxList';
import {
  coachOptions,
  filterByCoach,
  filterCounts,
  filterSourceState,
  filterWork,
  INBOX_FILTERS,
  InboxFilter,
  inboxGroups,
  InboxSegment,
  inSegment,
  segmentWork,
  withoutHidden,
} from './inboxModel';
import { InboxToolbar } from './InboxToolbar';

const narrowScreen = () => typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 899px)').matches;

const SEGMENT_NOUN: Record<InboxSegment, string> = { programme: 'milestones', success: 'success updates' };

/**
 * One queue for everything that needs doing, a row per client, worst first,
 * split into milestones and success updates so the six-weekly updates
 * don't bury programme deadlines. Selecting a row opens that client's tasks
 * beside the list (below it on a phone).
 */
export const InboxView: React.FC<{
  tasks: ClientTasksResult;
  actions: TaskActions;
  /** Above the heading for team scopes; none for a coach. */
  scopeLabel: string | null;
  selectedId: string | null;
  onSelect: (clientId: string) => void;
  segment: InboxSegment;
  onSegment: (segment: InboxSegment) => void;
  /** Lead-coach filter, for team scopes only. */
  coach?: { value: string | null; onChange: (coach: string | null) => void };
  /** Name each client's lead coach; off for a coach, whose clients are all their own. */
  showCoach?: boolean;
}> = ({ tasks, actions, scopeLabel, selectedId, onSelect, segment, onSegment, coach, showCoach = true }) => {
  const [filter, setFilter] = useState<InboxFilter>('all');
  const [search, setSearch] = useState('');
  const detailRef = useRef<HTMLDivElement>(null);
  const now = new Date();
  const coachValue = coach?.value ?? null;

  const all = useMemo(() => withoutHidden(tasks.work, actions.hiddenTaskIds), [tasks.work, actions.hiddenTaskIds]);
  const bySegment = useMemo(() => ({ programme: segmentWork(all, 'programme'), success: segmentWork(all, 'success') }), [all]);
  const segmentCounts = {
    programme: filterByCoach(bySegment.programme, coachValue).length,
    success: filterByCoach(bySegment.success, coachValue).length,
  };
  const coaches = useMemo(() => coachOptions(bySegment[segment]), [bySegment, segment]);
  const coached = useMemo(() => filterByCoach(bySegment[segment], coachValue), [bySegment, segment, coachValue]);
  const counts = useMemo(() => filterCounts(coached), [coached]);
  const shown = useMemo(() => filterWork(coached, segment === 'programme' ? filter : 'all', search), [coached, segment, filter, search]);
  const groups = useMemo(() => inboxGroups(shown, segment), [shown, segment]);
  // Without a selection, open the top row as displayed (groups can reorder the sorted list).
  const selected = shown.find((item) => item.client.clientId === selectedId) ?? groups[0]?.items[0] ?? null;
  const otherTasks = selected ? (all.find((item) => item.client.clientId === selected.client.clientId)?.tasks ?? []).filter((task) => !inSegment(task, segment)) : [];
  const stillChecking = Object.values(tasks.sources).some((s) => s === 'loading');
  const anyFailed = Object.values(tasks.sources).some((s) => s === 'error');
  const otherSegment: InboxSegment = segment === 'programme' ? 'success' : 'programme';
  const coachName = coaches.find((option) => option.key === coachValue)?.name;

  const select = (clientId: string) => {
    onSelect(clientId);
    if (narrowScreen()) requestAnimationFrame(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const emptyPanel = (title: string, body: React.ReactNode, action?: React.ReactNode) => (
    <Box sx={{ ...panelSx, py: 7, px: 3, textAlign: 'center' }}>
      <Typography component="h2" sx={{ fontSize: 20, fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography sx={{ color: INK.body, mt: 1, maxWidth: 460, mx: 'auto' }}>{body}</Typography>
      {action ? <Box sx={{ mt: 2 }}>{action}</Box> : null}
    </Box>
  );

  const renderBody = () => {
    if (tasks.status === 'error') {
      return (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={tasks.refresh}>Retry</Button>}>
          {tasks.error}
        </Alert>
      );
    }
    if (tasks.status === 'loading' && tasks.clients.length === 0) {
      return (
        <Box sx={{ ...panelSx, p: 2 }} aria-busy="true" aria-label="Loading clients">
          {[0, 1, 2, 3, 4].map((i) => (
            <Box key={i} sx={{ py: 1.25 }}>
              <Skeleton width="38%" height={18} />
              <Skeleton width="24%" height={14} />
            </Box>
          ))}
        </Box>
      );
    }
    if (all.length === 0) {
      return emptyPanel(
        stillChecking ? 'Nothing so far' : anyFailed ? 'Nothing found in what loaded' : 'Nothing needs you right now',
        stillChecking
          ? 'Still checking the rest. Anything that needs doing will appear here.'
          : anyFailed
            ? 'Some information did not load, so this may not be everything.'
            : 'Every client in scope is up to date.'
      );
    }
    if (coached.length === 0) {
      const waiting = segmentCounts[otherSegment];
      return emptyPanel(
        `No ${SEGMENT_NOUN[segment]}${coachName || coachValue ? ` for ${coachName ?? 'this coach'}` : ''}`,
        waiting > 0 ? `${waiting} client${waiting === 1 ? ' has' : 's have'} ${SEGMENT_NOUN[otherSegment]} waiting.` : 'Nothing else is waiting either.',
        waiting > 0 ? <Button variant="outlined" onClick={() => onSegment(otherSegment)}>Show {SEGMENT_NOUN[otherSegment]}</Button> : null
      );
    }
    if (shown.length === 0) {
      return emptyPanel(
        'No matches',
        `No clients match ${search.trim() ? `"${search.trim()}"` : 'this filter'}.`,
        <Button onClick={() => { setFilter('all'); setSearch(''); }}>Clear search and filters</Button>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
        <Box component="section" aria-label="Clients with actions" sx={{ ...panelSx, flex: '999 1 560px', minWidth: 0, overflow: 'hidden' }}>
          <InboxList groups={groups} selectedId={selected?.client.clientId ?? null} onSelect={select} now={now} showCoach={showCoach} />
        </Box>
        <Box ref={detailRef} sx={{ flex: '1 1 380px', minWidth: 0, position: { md: 'sticky' }, top: { md: 88 }, maxHeight: { md: 'calc(100vh - 104px)' }, overflowY: { md: 'auto' } }}>
          {selected ? <ClientDetailPanel key={selected.client.clientId} item={selected} otherTasks={otherTasks} actions={actions} now={now} showCoach={showCoach} /> : null}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 2.5 }}>
        <Box>
          {scopeLabel ? <Typography sx={{ fontSize: 13, fontWeight: 500, color: INK.muted, mb: 0.5 }}>{scopeLabel}</Typography> : null}
          <Typography component="h1" sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 600, letterSpacing: '-0.02em' }}>
            Actions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: INK.muted, fontSize: 13 }}>
          {tasks.refreshing ? 'Updating…' : tasks.error && tasks.status === 'ready' ? tasks.error : tasks.loadedAt ? `Updated ${format(tasks.loadedAt, 'HH:mm')}` : null}
          <Tooltip title="Refresh">
            <IconButton aria-label="Refresh" onClick={tasks.refresh} size="small">
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <SourceNotice sources={tasks.sources} failures={tasks.failures} onRetry={tasks.refresh} />
      </Box>

      <Box sx={{ mb: 2 }}>
        <InboxToolbar
          segment={segment}
          onSegment={onSegment}
          segmentCounts={segmentCounts}
          sources={tasks.sources}
          coachFilter={coach ? { options: coaches, total: bySegment[segment].length, value: coachValue, onChange: coach.onChange } : undefined}
          search={search}
          onSearch={setSearch}
        />
      </Box>

      {segment === 'programme' && coached.length > 0 ? (
        <Box role="group" aria-label="Filter by task" sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5, mb: 2.5 }}>
          {INBOX_FILTERS.map(({ key, label }) => {
            const on = filter === key;
            const state = filterSourceState(key, tasks.sources);
            return (
              <ButtonBase
                key={key}
                aria-pressed={on}
                title={state === 'error' ? `${label}: some of this information did not load` : undefined}
                onClick={() => setFilter(key)}
                sx={{
                  flexShrink: 0,
                  height: 32,
                  px: 1.5,
                  borderRadius: 999,
                  border: `1px solid ${on ? INK.main : state === 'error' ? '#E8C77A' : '#D5DCE5'}`,
                  bgcolor: on ? INK.main : '#FFFFFF',
                  color: on ? '#F8FAFC' : INK.body,
                  fontSize: 13,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                }}
              >
                {label}
                <Box component="span" sx={{ ml: 0.75, opacity: 0.72 }}>
                  {state === 'error' ? '-' : counts[key]}
                  {state === 'loading' ? '…' : ''}
                </Box>
              </ButtonBase>
            );
          })}
        </Box>
      ) : null}

      {renderBody()}
    </Box>
  );
};
